import './ImportAnonFeedsPage.css';
import '../common-style.css';

import { useContext, useState } from 'react';
import { AppState } from '../../context/general';
import { RouterContext, redirect, getLocation } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal, List, ListCheckboxItem } from 't-lib-ui/structure';
import { Checkbox, TextButton, ErrorNote } from 't-lib-ui/widgets';
import { getAnonFeedIdsMissingFromAccount, getAnonFeedMeta, importAnonFeedsToAccount } from '../../connector/anon-migration';
import { clearAnonData, clearAnonFeedData } from '../../connector/feed-engine';
import { getPlanLimits } from '../../schema-copy/userPlans';
import { formatCategory } from '../../utils/formatting';
import { CategoryIcon } from '../../components/CategoryIcons';
import { getFeedIconUrl } from '../../schema-copy/feed-icon';
import FeedIcon from '../../components/FeedIcon';
import DownreadLogo from '../../components/DownreadLogo';

const statusErrorMessages = {
  connection: <>Importing the feeds failed. Please try again.</>,
};

// Post-login interstitial: offers to import the anonymous session's feeds that the freshly
// logged-in account doesn't have yet. Reached via getAnonImportPath() from the login pages.
const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const appState = useContext(AppState);
  useFlowFormGlobal();

  const location = getLocation(routerCtx);
  // Same open-redirect guard as Login.tsx: only same-origin absolute paths are honored.
  const returnParam = location.searchParams.get('return');
  const returnTo = returnParam && returnParam.startsWith('/') && !returnParam.startsWith('//') ? returnParam : '/a';

  const user = appState.state.currentUser;
  const auth = appState.state.authData;

  // Computed once on mount: after a successful import user.feeds may update, and the pending
  // list must not shrink mid-render before the page navigates away.
  const [pending] = useState(() => user ? getAnonFeedIdsMissingFromAccount(user.feeds) : []);
  const [meta] = useState(() => getAnonFeedMeta());

  const maxFeeds = getPlanLimits(user?.userPlan ?? 'free').maxFeeds;
  const remaining = Math.max(0, maxFeeds - (user?.feeds.length ?? 0));
  const overQuota = pending.length > remaining;

  const [selected, setSelected] = useState<Set<string>>(() => new Set(pending.slice(0, remaining)));
  const [removeAnon, setRemoveAnon] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'ok'|'connection'|'done'>('ok');

  if (!user || !auth)
    return redirect(routerCtx, '/login');
  // Nothing to offer: no missing feeds, or the plan quota is already full (getAnonImportPath
  // wouldn't send anyone here then, but the page is also reachable directly).
  if (!pending.length || !remaining || status == 'done')
    return redirect(routerCtx, returnTo);

  const toggleFeed = (id: string) => (checked: boolean) => {
    const next = new Set(selected);
    if (!checked)
      next.delete(id);
    else if (next.size < remaining)
      next.add(id);
    setSelected(next);
  };

  const importClicked = async () => {
    setBusy(true);
    setStatus('ok');
    try {
      await importAnonFeedsToAccount(auth, pending.filter(id => selected.has(id)));
      removeAnon ? clearAnonData() : clearAnonFeedData();
      // Hard load so the authenticated engine refetches GET /api/feeds with the new subscriptions.
      window.location.href = returnTo;
    } catch (e) {
      console.error('anon import failed', e);
      setStatus('connection');
      setBusy(false);
    }
  };

  const ignoreClicked = () => {
    if (removeAnon)
      clearAnonData();
    setStatus('done');
  };

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard importAnonCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /></a>
          </div>

          <div className="authIntro">
            This browser has {pending.length == 1 ? 'a feed' : `${pending.length} feeds`} from anonymous
            reading that {pending.length == 1 ? "isn't" : "aren't"} in your account yet.
            Would you like to import {pending.length == 1 ? 'it' : 'them'}?
          </div>

          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />

          <List className="importAnonList">
            {pending.map(id => (
              <ListCheckboxItem key={id} selected={selected.has(id)} onClick={toggleFeed(id)}>
                <div className="importAnonFeedRow">
                  {meta[id] && <FeedIcon src={getFeedIconUrl(meta[id], 32)} circular={meta[id].circularIcon} size={32} className="importAnonFeedIcon" />}
                  <div className="importAnonFeedText">
                    <span className="importAnonFeedTitle">{meta[id]?.title || id}</span>
                    {formatCategory(meta[id]?.category) && (
                      <span className="importAnonFeedCategory">
                        <CategoryIcon category={meta[id].category!} size={14} color="var(--gray-400)" />
                        {formatCategory(meta[id].category)}
                      </span>
                    )}
                  </div>
                </div>
              </ListCheckboxItem>
            ))}
          </List>

          {overQuota && (
            <div className="importAnonQuotaNote">
              Your plan allows {remaining == 1 ? 'only 1 more feed' : `only ${remaining} more feeds`},
              so not all feeds can be imported.
            </div>
          )}

          <div className="importAnonRemoveRow">
            <Checkbox checked={removeAnon} onToggle={setRemoveAnon} />
            <span className="importAnonRemoveLabel" onClick={() => setRemoveAnon(!removeAnon)}>
              Remove anonymous data from this browser
            </span>
          </div>

          <div className="importAnonButtons">
            <TextButton type="major" label={selected.size ? `Import ${selected.size == 1 ? '1 feed' : `${selected.size} feeds`}` : 'Import'}
              onClick={importClicked} busy={busy} enabled={selected.size ? 'enabled' : 'disabled'} />
            <TextButton type="minor" label="Ignore" onClick={ignoreClicked} enabled={busy ? 'disabled' : 'enabled'} />
          </div>
        </div>
      </div>
    </FlowForm>
  );
};

export default Page;
